import React from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { PortalUserDocument, ResendInvitationDocument } from '../../graphql/__generated__/types';
import { formatDate } from './display';
import { resendRefusalOf } from './resendRefusal';

interface Props {
    userId: string;
    email: string;
    /**
     * Whether a live activation link exists right now: read from the sheet, which
     * knows: `invitationExpiresAt` is filled for a **pending** invitation only.
     *
     * Attention, it decides whether this dialog warns about a link being revoked, and the
     * distinction is not cosmetic: `previousLinkRevoked` comes back **always true**
     * from the API, whether or not there was anything to revoke. Chained after a
     * reopening there never is: the closure had already cancelled the pending
     * invitation, and the warning would then describe a mailbox nobody has, which
     * is how an operator ends up telling somebody to ignore a mail that was never
     * sent.
     */
    hasLiveLink: boolean;
    onClose: () => void;
}

/**
 * The confirmation of « relancer l'invitation ».
 *
 * **Why a modal rather than a bare button.** The gesture is not idempotent and it has
 * a victim: it **rotates the token**, so the link already in the person's mailbox
 * stops working the instant it runs. Somebody may be opening that link right now.
 * Rotating rather than extending is the right call: an invitation token is a bearer
 * token, and pushing its expiry back on every resend would build a bearer of
 * unbounded life, but the API is explicit that the race it creates is answered
 * **by the message, never by a longer life**. This dialog is that message.
 *
 * So the revocation is said **twice, and deliberately**: before the click as a
 * consequence to accept, and after it as a fact to relay: the person must be told
 * to use the new mail and ignore the old one.
 *
 * **Attention, both sentences are gated on `hasLiveLink`** (PCG-94). The dialog is now also
 * reached with no pending invitation at all: chained after reopening an access,
 * which sends nothing and whose closure had already cancelled what was pending: and
 * the API answers `previousLinkRevoked: true` there too. Warning about a link that
 * never existed is not a harmless extra sentence: it tells the operator to warn
 * somebody about a mail they never received.
 */
const ResendInvitationDialog = ({ userId, email, hasLiveLink, onClose }: Props) => {
    const [resend, { data, loading, error }] = useMutation(ResendInvitationDocument, {
        // The sheet prints the expiry and the send count; both change here. Without
        // the refetch the operator would read the state they just left behind.
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const result = data?.resendInvitation;

    const submit = () => {
        // The refusal is read off `error` below; without this the rejected promise
        // reaches the console as an unhandled one on every refusal.
        resend({ variables: { userId } }).catch(() => {});
    };

    /* ------------------------------------------------------------------- done */
    if (result) {
        const expiry = result.expiresAt ? formatDate(result.expiresAt) : null;
        const sentCount = result.sentCount ?? undefined;

        return (
            <Modal open onClose={onClose} title={c('Resend invitation').t`Invitation sent again`}>
                <ModalContent>
                    {/* Two outcomes, and they are not the same news. The rotation is
                        acquired either way; the mail is what may still be in flight. */}
                    <Alert
                        type={result.activationEmailSent ? 'success' : 'warning'}
                        title={
                            result.activationEmailSent
                                ? c('Resend invitation').t`A new link has left for ${email}.`
                                : c('Resend invitation').t`The new link is issued, but the email has not left yet.`
                        }
                    >
                        <p className="text-sm">
                            {!result.activationEmailSent
                                ? c('Resend invitation')
                                      .t`The send failed and sits in the retry queue. The new link exists and is valid — if the email never arrives, send it again from here.`
                                : hasLiveLink
                                  ? c('Resend invitation')
                                        .t`Tell them to use this new email: the link from the previous one no longer works.`
                                  : // Nothing was in their mailbox to supersede, so
                                    // « utilisez le nouveau » would be a warning about
                                    // a mail they never got.
                                    c('Resend invitation')
                                        .t`This is the only activation link they hold: nothing was pending before it.`}
                        </p>
                    </Alert>

                    <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {expiry && (
                            <div>
                                <dt className="text-xs font-semibold tracking-wide text-text-600 uppercase">
                                    {c('Resend invitation').t`New link valid until`}
                                </dt>
                                {/* The deadline starts from zero, it is never
                                    extended: saying so here is what stops an operator
                                    reading a resend as « I bought a few more days ». */}
                                <dd className="mt-1 font-mono text-sm text-text-900">{expiry}</dd>
                            </div>
                        )}
                        {typeof sentCount === 'number' && (
                            <div>
                                <dt className="text-xs font-semibold tracking-wide text-text-600 uppercase">
                                    {c('Resend invitation').t`Invitations sent`}
                                </dt>
                                <dd className="mt-1 font-mono text-sm text-text-900">{sentCount}</dd>
                            </div>
                        )}
                    </dl>

                    {/* Said again, on the screen that reports success. The operator
                        is the only person who can warn the recipient, and they will
                        not go back to a dialog they have closed. */}
                    {/* `previousLinkRevoked` comes back true even when there was
                        no pending invitation to revoke, so the sheet's reading is
                        what gates it. */}
                    {result.previousLinkRevoked && hasLiveLink && (
                        <p className="mt-4 text-xs text-text-600">
                            {c('Resend invitation')
                                .t`The previous link has been revoked. If they open it, they will be told it is not valid — that is expected, and the new email is the one to use.`}
                        </p>
                    )}
                </ModalContent>
                <ModalFooter>
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Close`}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    /* ---------------------------------------------------------------- confirm */
    return (
        <Modal open onClose={onClose} title={c('Resend invitation').t`Send the invitation again`}>
            <ModalContent>
                <p className="text-xs text-text-600">
                    {c('Resend invitation')
                        .t`A new activation link will be issued and emailed to ${email}, with a fresh deadline.`}
                </p>

                {/* THE SENTENCE THIS DIALOG EXISTS FOR: when there is a link to
                    lose. It is a warning, not a detail: the consequence lands on
                    somebody who is not in the room. With no pending invitation there
                    is no victim and no warning to give, and printing it anyway would
                    send the operator chasing a mailbox nobody has. */}
                {hasLiveLink && (
                    <Alert
                        type="warning"
                        className="mt-4"
                        title={c('Resend invitation').t`The previous link stops working`}
                    >
                        <p className="text-sm">
                            {c('Resend invitation')
                                .t`The link already in their mailbox is revoked at once — including if they are opening it right now. Make sure they know to use the new email.`}
                        </p>
                    </Alert>
                )}

                {error && (
                    <div className="mt-4">
                        <RefusalNotice refusal={resendRefusalOf(error)} />
                    </div>
                )}
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="default" onClick={submit} loading={loading}>
                    {c('Resend invitation').t`Send again`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

/** Used by the sheet to phrase the button when the invitation has already been chased. */
export const resendButtonLabel = (sentCount: number): string =>
    sentCount > 1
        ? // No `ngettext`: this branch only runs above one, so a singular form would
          // be a plural rule written for a case that cannot occur.
          c('Resend invitation').t`Send again (${sentCount} sends so far)`
        : c('Resend invitation').t`Send the invitation again`;

export default ResendInvitationDialog;
